import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Collapse,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  emptyWorkflowGraph,
  findNode,
  portKindOfNodeType,
  validateWorkflowGraph,
  type ParamField,
  type WorkflowGraph,
  type WorkflowNode,
  type WorkflowNodeType,
  type WorkflowPortKind,
} from '@gw/shared';
import { useAllModels, useModelGroups, useSaveWorkflow, useWorkflow } from '../api/hooks';
import { extractErrorMessage } from '../api/client';
import { resolveModelParams } from '../components/provider/modelParams';
import WorkflowCanvas, { type NodeTargetInfo } from '../components/workflow/WorkflowCanvas';
import NodeConfigPanel from '../components/workflow/NodeConfigPanel';
import WorkflowParamsEditor from '../components/workflow/WorkflowParamsEditor';
import WorkflowTestTab from '../components/workflow/WorkflowTestTab';
import { NODE_TYPE_LABEL, PORT_COLOR } from '../components/workflow/nodes';
import { CategorySelect } from '../components/CategorySelect';

/** Loai dau ra suy tu category (giong gateway) — de to mau cong ra tren canvas. */
function outputKindOfCategory(category?: string | null): WorkflowPortKind {
  const c = (category || '').toLowerCase();
  if (c.includes('video') || c.includes('lipsync') || c.includes('caption') || c.includes('chuyển động')) return 'video';
  if (c.includes('văn bản') || c.includes('text') || c.includes('chat')) return 'text';
  return 'image';
}

const FUNCTION_NODES: WorkflowNodeType[] = ['text', 'image', 'video'];

function PaletteItem({ label, color, payload }: { label: string; color: string; payload: unknown }) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/gw-node', JSON.stringify(payload));
        e.dataTransfer.effectAllowed = 'move';
      }}
      style={{
        border: `1px solid ${color}`,
        borderLeft: `4px solid ${color}`,
        borderRadius: 8,
        padding: '6px 10px',
        marginBottom: 6,
        cursor: 'grab',
        background: '#fff',
        fontSize: 13,
      }}
      title="Kéo thả vào sơ đồ"
    >
      {label}
    </div>
  );
}

export default function WorkflowEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const wfQuery = useWorkflow(id);
  const save = useSaveWorkflow();
  const models = useAllModels();
  const groups = useModelGroups();

  const [tab, setTab] = useState('canvas');
  const [graph, setGraph] = useState<WorkflowGraph>(emptyWorkflowGraph());
  const [params, setParams] = useState<ParamField[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [meta, setMeta] = useState({ name: '', slug: '', category: '', description: '', status: 'disabled', retryCount: 0 });
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const w = wfQuery.data;
    if (!w || loaded) return;
    setGraph((w.graph as WorkflowGraph) ?? emptyWorkflowGraph());
    setParams((w.paramsSchema as ParamField[]) ?? []);
    setMeta({
      name: w.name,
      slug: w.slug,
      category: w.category ?? '',
      description: w.description ?? '',
      status: w.status,
      retryCount: w.retryCount ?? 0,
    });
    setLoaded(true);
  }, [wfQuery.data, loaded]);

  const errors = useMemo(() => validateWorkflowGraph(graph), [graph]);
  const invalidIds = useMemo(() => {
    const set = new Set<string>();
    for (const n of graph.nodes) {
      if ((n.type === 'model' || n.type === 'group') && !n.ref) set.add(n.id);
    }
    return set;
  }, [graph]);

  /** Thong tin model/nhom cho 1 node (schema de ve cong vao + ten + loai dau ra). */
  const resolveTarget = useMemo(() => {
    const modelById = new Map((models.data ?? []).map((m) => [m.id, m]));
    const groupById = new Map((groups.data ?? []).map((g) => [g.id, g]));
    return (node: WorkflowNode): NodeTargetInfo | null => {
      if (node.type === 'model' && node.ref) {
        const m = modelById.get(node.ref);
        if (!m) return null;
        return {
          schema: resolveModelParams(m),
          name: `${m.provider?.name ?? ''} · ${m.name}`,
          outputKind: outputKindOfCategory(m.category),
        };
      }
      if (node.type === 'group' && node.ref) {
        const g = groupById.get(node.ref);
        if (!g) return null;
        return {
          schema: (g.paramsSchema as ParamField[]) ?? [],
          name: g.name,
          outputKind: outputKindOfCategory(g.category),
        };
      }
      return null;
    };
  }, [models.data, groups.data]);

  const modelOptions = useMemo(
    () => (models.data ?? []).map((m) => ({ value: m.id, label: `${m.provider?.name ?? '?'} · ${m.name}` })),
    [models.data],
  );
  const groupOptions = useMemo(
    () => (groups.data ?? []).map((g) => ({ value: g.id, label: g.name })),
    [groups.data],
  );

  const q = search.trim().toLowerCase();
  const filteredGroups = q ? groupOptions.filter((o) => o.label.toLowerCase().includes(q)) : groupOptions;

  /**
   * Model trong bảng chọn được GOM THEO NHÀ CUNG CẤP — danh sách phẳng vài chục model
   * rất khó tìm. Mỗi nhà cung cấp là một mục gập/mở; ô tìm kiếm lọc xuyên qua các nhóm.
   */
  const modelsByProvider = useMemo(() => {
    const bucket = new Map<string, Array<{ value: string; label: string }>>();
    for (const m of models.data ?? []) {
      const provider = m.provider?.name ?? 'Khác';
      const label = m.name;
      if (q && !`${provider} ${label}`.toLowerCase().includes(q)) continue;
      if (!bucket.has(provider)) bucket.set(provider, []);
      bucket.get(provider)!.push({ value: m.id, label });
    }
    return Array.from(bucket.entries())
      .map(([provider, items]) => ({ provider, items: items.sort((a, b) => a.label.localeCompare(b.label, 'vi')) }))
      .sort((a, b) => a.provider.localeCompare(b.provider, 'vi'));
  }, [models.data, q]);

  const modelMatchCount = modelsByProvider.reduce((s, g) => s + g.items.length, 0);

  const selected = selectedId ? findNode(graph, selectedId) : undefined;

  const patchNode = (nodeId: string, patch: Partial<WorkflowNode>) =>
    setGraph((g) => {
      let edges = g.edges;
      // Xoa 1 truong hop dieu kien -> xoa luon day noi cua cong "case:<id>" do.
      if (patch.cases) {
        const alive = new Set(patch.cases.map((c) => `case:${c.id}`));
        edges = edges.filter((e) => e.from !== nodeId || !e.fromPort.startsWith('case:') || alive.has(e.fromPort));
      }
      return { ...g, edges, nodes: g.nodes.map((n) => (n.id === nodeId ? { ...n, ...patch } : n)) };
    });

  const deleteNode = (nodeId: string) => {
    setGraph((g) => ({
      ...g,
      nodes: g.nodes.filter((n) => n.id !== nodeId),
      edges: g.edges.filter((e) => e.from !== nodeId && e.to !== nodeId),
    }));
    setSelectedId(null);
  };

  /**
   * "LAY THAM SO TU INPUT CONG KHAI": lam workflow lo API y het model/nhom cua node nay.
   *  - Moi field trong schema cua node: neu dang tro toi node CON TON TAI -> giu (day noi noi bo);
   *    con lai (gia tri co dinh / rong / tro node DA XOA) -> tro ve {{input.<ten>}}.
   *  - Field chua co trong paramsSchema cua workflow -> tu khai bao; gia tri co dinh cu thanh default.
   */
  const exposeNodeParams = (nodeId: string) => {
    const node = findNode(graph, nodeId);
    const target = node ? resolveTarget(node) : null;
    if (!node || !target) return;
    const nodeIds = new Set(graph.nodes.map((n) => n.id));
    const declared = new Set(params.map((p) => p.name));
    const addedFields: ParamField[] = [];
    const newNodeParams: Record<string, unknown> = { ...(node.params ?? {}) };
    let bound = 0;

    for (const f of target.schema) {
      if (f.enabled === false) continue;
      const cur = newNodeParams[f.name];
      // Placeholder tro toi node CON TON TAI = day noi noi bo co chu dich -> giu nguyen.
      if (typeof cur === 'string' && cur.includes('{{nodes.')) {
        const refs = Array.from(cur.matchAll(/\{\{\s*nodes\.([^.\s}]+)/g)).map((m) => m[1]);
        if (refs.length && refs.every((id) => nodeIds.has(id))) continue;
      }
      // Gia tri co dinh hien tai -> thanh DEFAULT cua tham so cong khai.
      const literal = cur !== undefined && cur !== '' && !(typeof cur === 'string' && cur.includes('{{')) ? cur : undefined;
      if (!declared.has(f.name)) {
        addedFields.push({ ...f, ...(literal !== undefined ? { default: literal } : {}) });
        declared.add(f.name);
      }
      newNodeParams[f.name] = `{{input.${f.name}}}`;
      bound += 1;
    }

    if (addedFields.length) setParams((p) => [...p, ...addedFields]);
    patchNode(nodeId, { params: newNodeParams });
    message.success(
      `Đã trỏ ${bound} tham số về input công khai${addedFields.length ? ` · khai báo thêm ${addedFields.length} tham số cho API` : ''}. Nhớ bấm Lưu.`,
    );
  };

  /** Nhan ban node: copy toan bo cau hinh, id moi, lech vi tri; KHONG keo theo day noi. */
  const duplicateNode = (nodeId: string) => {
    const src = findNode(graph, nodeId);
    if (!src || src.type === 'start' || src.type === 'end') return;
    const stamp = Date.now().toString(36);
    const clone: WorkflowNode = {
      ...JSON.parse(JSON.stringify(src)),
      id: `n${stamp}d`,
      name: `${src.name || ''} (bản sao)`.trim(),
      position: { x: src.position.x + 60, y: src.position.y + 60 },
      // Node dieu kien: cap id case MOI — id case la cong ra, trung id se lan day voi node goc.
      ...(src.cases ? { cases: src.cases.map((c, i) => ({ ...c, id: `c${stamp}${i}` })) } : {}),
    };
    setGraph((g) => ({ ...g, nodes: [...g.nodes, clone] }));
    setSelectedId(clone.id);
  };

  const onSave = async () => {
    // Đang tắt = bản nháp: lưu được dù sơ đồ chưa xong. Chỉ chặn khi muốn BẬT.
    if (errors.length && meta.status === 'active') {
      message.error(`Chưa bật được vì sơ đồ chưa hợp lệ: ${errors[0]}`);
      return;
    }
    if (!meta.name.trim() || !meta.slug.trim()) {
      message.error('Cần nhập Tên và Slug ở tab Thông tin');
      setTab('info');
      return;
    }
    try {
      await save.mutateAsync({
        id,
        data: {
          slug: meta.slug.trim(),
          name: meta.name.trim(),
          category: meta.category || null,
          description: meta.description || null,
          status: meta.status as 'active' | 'disabled',
          retryCount: meta.retryCount,
          graph,
          paramsSchema: params.filter((p) => p.name),
        },
      });
      message.success('Đã lưu workflow');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  if (wfQuery.isLoading || !loaded) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/workflows')} />
          <Typography.Title level={4} style={{ margin: 0 }}>
            {meta.name || 'Workflow'}
          </Typography.Title>
          <code>{meta.slug}</code>
          {meta.status === 'active' ? <Tag color="success">Đang bật</Tag> : <Tag>Đang tắt</Tag>}
        </Space>
        <Space>
          {errors.length > 0 && (
            <Tag color={meta.status === 'active' ? 'error' : 'warning'}>
              {errors.length} lỗi sơ đồ{meta.status === 'active' ? '' : ' (bản nháp)'}
            </Tag>
          )}
          <Button type="primary" icon={<SaveOutlined />} loading={save.isPending} onClick={onSave}>
            Lưu
          </Button>
        </Space>
      </Space>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: 'canvas', label: 'Sơ đồ' },
          { key: 'info', label: 'Thông tin & tham số' },
          { key: 'test', label: 'Chạy thử' },
        ]}
      />

      {tab === 'canvas' && (
        <div style={{ display: 'flex', gap: 12, height: 'calc(100vh - 250px)', minHeight: 480 }}>
          {/* Bảng chọn node — kéo thả vào sơ đồ */}
          <Card size="small" style={{ width: 260, overflowY: 'auto' }} styles={{ body: { padding: 10 } }}>
            <Typography.Text strong style={{ fontSize: 12 }}>
              NODE CHỨC NĂNG
            </Typography.Text>
            <div style={{ marginTop: 6 }}>
              {FUNCTION_NODES.map((t) => (
                <PaletteItem
                  key={t}
                  label={NODE_TYPE_LABEL[t]}
                  color={PORT_COLOR[portKindOfNodeType(t) ?? 'text']}
                  payload={{ type: t, name: NODE_TYPE_LABEL[t] }}
                />
              ))}
              <PaletteItem
                label="Điều kiện (rẽ nhánh)"
                color="#0891b2"
                payload={{ type: 'condition', name: 'Điều kiện' }}
              />
            </div>

            <Input.Search
              placeholder="Tìm model / nhóm…"
              allowClear
              size="small"
              style={{ margin: '10px 0 6px' }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <Typography.Text strong style={{ fontSize: 12 }}>
              NHÓM MODEL ({filteredGroups.length})
            </Typography.Text>
            <div style={{ marginTop: 6 }}>
              {filteredGroups.slice(0, 40).map((g) => (
                <PaletteItem key={g.value} label={g.label} color="#f59e0b" payload={{ type: 'group', ref: g.value, name: g.label }} />
              ))}
            </div>

            <Typography.Text strong style={{ fontSize: 12 }}>
              MODEL THEO NHÀ CUNG CẤP ({modelMatchCount})
            </Typography.Text>
            <Collapse
              size="small"
              ghost
              style={{ marginTop: 6 }}
              // Đang tìm kiếm thì mở sẵn mọi nhà cung cấp còn kết quả.
              activeKey={q ? modelsByProvider.map((g) => g.provider) : undefined}
              defaultActiveKey={[]}
              items={modelsByProvider.map((g) => ({
                key: g.provider,
                label: (
                  <span style={{ fontSize: 12 }}>
                    <b>{g.provider}</b> <Typography.Text type="secondary">({g.items.length})</Typography.Text>
                  </span>
                ),
                children: (
                  <div>
                    {g.items.map((m) => (
                      <PaletteItem
                        key={m.value}
                        label={m.label}
                        color="#6366f1"
                        payload={{ type: 'model', ref: m.value, name: `${g.provider} · ${m.label}` }}
                      />
                    ))}
                  </div>
                ),
              }))}
            />
            {modelMatchCount === 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Không có model nào khớp.
              </Typography.Text>
            )}
          </Card>

          {/* Canvas */}
          <Card size="small" style={{ flex: 1 }} styles={{ body: { padding: 0, height: '100%' } }}>
            <WorkflowCanvas
              graph={graph}
              onChange={setGraph}
              resolveTarget={resolveTarget}
              selectedId={selectedId}
              onSelect={setSelectedId}
              invalidIds={invalidIds}
            />
          </Card>
        </div>
      )}

      {tab === 'canvas' && errors.length > 0 && (
        <Alert
          style={{ marginTop: 10 }}
          type="warning"
          showIcon
          message="Sơ đồ chưa hợp lệ — vẫn lưu được dưới dạng bản nháp, nhưng chưa bật được"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          }
        />
      )}

      {tab === 'info' && (
        <Card>
          <Form layout="vertical">
            <Space style={{ display: 'flex' }} align="start" size="large">
              <Form.Item label="Tên" required style={{ flex: 1 }}>
                <Input value={meta.name} onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))} />
              </Form.Item>
              <Form.Item
                label="Slug"
                required
                tooltip="Phần mềm thứ 3 có thể gọi bằng slug này. Không được trùng slug của nhóm model."
                style={{ flex: 1 }}
              >
                <Input value={meta.slug} onChange={(e) => setMeta((m) => ({ ...m, slug: e.target.value.trim() }))} />
              </Form.Item>
            </Space>
            <Space style={{ display: 'flex' }} align="start" size="large">
              <Form.Item label="Danh mục" style={{ flex: 1, minWidth: 220 }}>
                <CategorySelect value={meta.category} onChange={(v) => setMeta((m) => ({ ...m, category: v ?? '' }))} />
              </Form.Item>
              <Form.Item label="Trạng thái" style={{ flex: 1, minWidth: 180 }}>
                <Select
                  value={meta.status}
                  onChange={(v) => setMeta((m) => ({ ...m, status: v }))}
                  options={[
                    { value: 'active', label: 'Đang bật' },
                    { value: 'disabled', label: 'Đang tắt' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Số lần chạy lại khi lỗi"
                tooltip="Khi một bước lỗi mà KHÔNG có nhánh 'thất bại', workflow sẽ chạy lại TOÀN BỘ từ đầu, tối đa số lần này. 0 = tắt."
                style={{ flex: 1, minWidth: 180 }}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  value={meta.retryCount}
                  onChange={(v) => setMeta((m) => ({ ...m, retryCount: Number(v ?? 0) }))}
                />
              </Form.Item>
            </Space>
            <Form.Item label="Mô tả">
              <Input.TextArea
                rows={2}
                value={meta.description}
                onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))}
              />
            </Form.Item>
          </Form>

          <Typography.Title level={5} style={{ marginTop: 8 }}>
            Tham số đầu vào (công khai)
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            Đây chính là thứ phần mềm thứ 3 nhìn thấy trong <code>/api/v1/catalog</code>. Dùng tên canonical
            (<code>prompt</code>, <code>input_image</code>, <code>reference_images</code>, <code>aspect_ratio</code>…).
            Trong các node phía sau, tham chiếu bằng <code>{'{{input.<tên>}}'}</code>.
          </Typography.Paragraph>
          <WorkflowParamsEditor value={params} onChange={setParams} />
        </Card>
      )}

      {tab === 'test' && id && (
        <Card>
          <WorkflowTestTab workflowId={id} params={params} />
        </Card>
      )}

      {/* Ngăn cấu hình node */}
      <Drawer
        title={selected ? `Cấu hình: ${selected.name || selected.id}` : ''}
        placement="right"
        width={460}
        open={!!selected && tab === 'canvas'}
        onClose={() => setSelectedId(null)}
        mask={false}
      >
        {selected && (
          <NodeConfigPanel
            graph={graph}
            node={selected}
            onChange={(patch) => patchNode(selected.id, patch)}
            onDelete={() => deleteNode(selected.id)}
            onDuplicate={() => duplicateNode(selected.id)}
            onExposeParams={() => exposeNodeParams(selected.id)}
            workflowParams={params}
            target={resolveTarget(selected)}
            resolveTarget={resolveTarget}
            modelOptions={modelOptions}
            groupOptions={groupOptions}
          />
        )}
      </Drawer>
    </div>
  );
}
